import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Bot, Send, User as UserIcon, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AIChatPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState([
    {
      id: 'm-init',
      role: 'assistant',
      content: { ar: 'مرحباً! أنا مساعدك الطبي. كيف يمكنني مساعدتك اليوم؟', en: 'Hello! I am your medical assistant. How can I help you today?', fr: 'Bonjour! Comment puis-je vous aider?' }
    }
  ]);
  const msgSeq = useRef(0);
  const nextMsgId = () => {
    msgSeq.current += 1;
    return `m-${Date.now()}-${msgSeq.current}`;
  };
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(`patient_${user?._id || 'guest'}_${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || streaming) return;

    const userMsg = input.trim();
    setInput('');
    setMessages((m) => [...m, { id: nextMsgId(), role: 'user', content: userMsg }]);
    setStreaming(true);
    const assistantIdx = messages.length + 1;
    const assistantId = nextMsgId();
    setMessages((m) => [...m, { id: assistantId, role: 'assistant', content: '' }]);

    try {
      const resp = await fetch(`${API}/ai/analyze-symptoms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMsg, session_id: sessionId.current })
      });

      if (!resp.ok) throw new Error('AI request failed');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter((l) => l.startsWith('data: '));
        for (const line of lines) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          acc += data;
          setMessages((m) => {
            const next = [...m];
            next[assistantIdx] = { id: assistantId, role: 'assistant', content: acc };
            return next;
          });
        }
      }
    } catch (err) {
      setMessages((m) => {
        const next = [...m];
        next[assistantIdx] = { id: assistantId, role: 'assistant', content: t('عذراً، حدث خطأ. يرجى المحاولة لاحقاً.', 'Sorry, something went wrong. Please try again later.', 'Désolé, une erreur est survenue.') };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const renderContent = (c) => typeof c === 'object' ? (c[language] || c.en) : c;

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-6">
          <div className="inline-flex w-16 h-16 bg-gradient-to-br from-[#1e88e5] to-[#1565c0] rounded-2xl items-center justify-center mb-3">
            <Bot size={32} className="text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#2c3e50]" data-testid="ai-chat-title">
            {t('المساعد الطبي الذكي', 'AI Medical Assistant', 'Assistant Médical IA')}
          </h1>
          <p className="text-gray-600 text-sm">{t('بدعم من Claude Sonnet 4.5', 'Powered by Claude Sonnet 4.5', 'Propulsé par Claude Sonnet 4.5')}</p>
        </div>

        <Alert className="mb-4 bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-4 w-4 text-yellow-700" />
          <AlertDescription className="text-yellow-800 text-sm">
            {t(
              'هذا المساعد لا يحل محل الاستشارة الطبية. لتشخيص دقيق، احجز مع طبيب.',
              'This assistant does not replace medical consultation. For accurate diagnosis, book with a doctor.',
              'Cet assistant ne remplace pas une consultation médicale.'
            )}
          </AlertDescription>
        </Alert>

        <Card className="flex flex-col h-[60vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <Avatar className={msg.role === 'user' ? 'bg-[#1e88e5]' : 'bg-gradient-to-br from-[#1e88e5] to-[#1565c0]'}>
                  <AvatarFallback className="text-white">
                    {msg.role === 'user' ? <UserIcon size={18} /> : <Bot size={18} />}
                  </AvatarFallback>
                </Avatar>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                  msg.role === 'user' ? 'bg-[#1e88e5] text-white' : 'bg-gray-100 text-gray-900'
                }`}>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{renderContent(msg.content) || (streaming && i === messages.length - 1 ? '...' : '')}</p>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSend} className="border-t p-4 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('اكتب رسالتك...', 'Type your message...', 'Tapez votre message...')}
              disabled={streaming}
              data-testid="chat-input"
              className="flex-1"
            />
            <Button type="submit" disabled={streaming || !input.trim()} className="bg-[#1e88e5] hover:bg-[#1565c0]" data-testid="chat-send">
              <Send size={18} />
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};
